import React from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Clock,
  Cloud,
  Database,
  Globe,
  HardDrive,
  Layers,
  Lock,
  Rocket,
  Search,
  Server,
  Shield,
  Zap,
} from 'lucide-react';
import { opsAccentBadge, opsSubNavClass, opsSubNavItem } from './operationsUi';

export type OperationsTabId =
  | 'overview'
  | 'backend'
  | 'database'
  | 'errors'
  | 'apis'
  | 'external'
  | 'auth'
  | 'notifications'
  | 'scheduler'
  | 'queue'
  | 'storage'
  | 'security'
  | 'analytics'
  | 'logs'
  | 'deployments'
  | 'uptime';

export const OPERATIONS_TABS: Array<{
  id: OperationsTabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
}> = [
  { id: 'overview', label: 'Overview', icon: Activity, group: 'Command' },
  { id: 'backend', label: 'Backend', icon: Server, group: 'Command' },
  { id: 'database', label: 'Database', icon: Database, group: 'Command' },
  { id: 'errors', label: 'Errors', icon: AlertTriangle, group: 'Observe' },
  { id: 'logs', label: 'Live logs', icon: Search, group: 'Observe' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'Observe' },
  { id: 'apis', label: 'Internal APIs', icon: Zap, group: 'Services' },
  { id: 'external', label: 'External', icon: Globe, group: 'Services' },
  { id: 'auth', label: 'Auth', icon: Lock, group: 'Services' },
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'Platform' },
  { id: 'scheduler', label: 'Scheduler', icon: Clock, group: 'Platform' },
  { id: 'queue', label: 'Queues', icon: Layers, group: 'Platform' },
  { id: 'storage', label: 'Storage', icon: HardDrive, group: 'Platform' },
  { id: 'security', label: 'Security', icon: Shield, group: 'Platform' },
  { id: 'deployments', label: 'Deploy', icon: Rocket, group: 'Release' },
  { id: 'uptime', label: 'Uptime', icon: Cloud, group: 'Release' },
];

const TAB_ICONS: Record<OperationsTabId, React.ReactNode> = {
  overview: <Activity className="h-5 w-5" />,
  backend: <Server className="h-5 w-5" />,
  database: <Database className="h-5 w-5" />,
  errors: <AlertTriangle className="h-5 w-5" />,
  logs: <Search className="h-5 w-5" />,
  analytics: <BarChart3 className="h-5 w-5" />,
  apis: <Zap className="h-5 w-5" />,
  external: <Globe className="h-5 w-5" />,
  auth: <Lock className="h-5 w-5" />,
  notifications: <Bell className="h-5 w-5" />,
  scheduler: <Clock className="h-5 w-5" />,
  queue: <Layers className="h-5 w-5" />,
  storage: <HardDrive className="h-5 w-5" />,
  security: <Shield className="h-5 w-5" />,
  deployments: <Rocket className="h-5 w-5" />,
  uptime: <Cloud className="h-5 w-5" />,
};

interface OperationsSidebarProps {
  activeTab: OperationsTabId;
  onTabChange: (tab: OperationsTabId) => void;
}

const OperationsSidebar: React.FC<OperationsSidebarProps> = ({ activeTab, onTabChange }) => (
  <aside className={opsSubNavClass} aria-label="Operations modules">
    <div className={`mb-2 flex h-10 w-10 items-center justify-center rounded-xl ${opsAccentBadge}`}>
      <Activity className="h-5 w-5" />
    </div>
    {OPERATIONS_TABS.map((tab) => (
      <button
        key={tab.id}
        type="button"
        title={tab.label}
        onClick={() => onTabChange(tab.id)}
        className={opsSubNavItem(activeTab === tab.id)}
      >
        {TAB_ICONS[tab.id]}
      </button>
    ))}
  </aside>
);

export default OperationsSidebar;

export const OperationsMobileTabs: React.FC<OperationsSidebarProps> = ({ activeTab, onTabChange }) => (
  <div className="flex shrink-0 gap-0 overflow-x-auto border-b border-gray-200 bg-[#f0f2f5] dark:border-gray-700 dark:bg-[#202c33] md:hidden">
    {OPERATIONS_TABS.map((tab) => (
      <button
        key={tab.id}
        type="button"
        onClick={() => onTabChange(tab.id)}
        className={`shrink-0 px-3 py-2.5 text-xs font-medium whitespace-nowrap ${
          activeTab === tab.id
            ? 'border-b-2 border-primary-600 text-primary-600'
            : 'text-gray-600 dark:text-gray-300'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);
